<template>
  <div
    v-if="agentAnswer"
    data-testid="agent-response-section"
    class="rounded-lg border border-secondary-300 bg-secondary-100 p-4"
  >
    <div class="flex items-start justify-between mb-2">
      <h3
        class="text-sm font-medium text-secondary-800 flex items-center gap-2"
      >
        <svg
          data-testid="translation-icon"
          class="h-4 w-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129"
          ></path>
        </svg>
        {{ t('response.agentResponseTitle') }}
      </h3>
      <div class="flex gap-2">
        <button
          v-if="isEditing"
          data-testid="translate-button"
          class="text-xs bg-green-600 hover:bg-green-700 text-white px-3 py-1 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
          :disabled="isTranslating || !editableText.trim()"
          @click="translateText"
        >
          {{
            isTranslating
              ? t('response.translating')
              : t('response.saveAndTranslate')
          }}
        </button>
        <button
          data-testid="edit-button"
          class="text-xs bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded-md transition-colors cursor-pointer"
          @click="toggleEditMode"
        >
          {{ isEditing ? t('response.cancel') : t('response.edit') }}
        </button>
      </div>
    </div>

    <!-- Display mode -->
    <div
      v-if="!isEditing"
      class="text-secondary-800 leading-relaxed markdown-content"
      v-html="renderedMarkdown"
    ></div>

    <!-- Edit mode -->
    <div v-if="isEditing" class="space-y-3">
      <label for="agent-response-edit" class="sr-only">
        {{ t('response.agentResponseTitle') }}
      </label>
      <textarea
        id="agent-response-edit"
        v-model="editableText"
        data-testid="edit-textarea"
        class="w-full p-3 border border-secondary-300 rounded-md text-secondary-800 leading-relaxed resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        rows="4"
        :placeholder="t('response.translationPlaceholder')"
      ></textarea>

      <!-- Error display -->
      <div
        v-if="translationError"
        data-testid="translation-error"
        class="text-red-600 text-sm"
      >
        {{ translationError }}
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, onMounted, onUnmounted, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { BackendWebSocketService } from '@/services/BackendWebSocketService';
import { renderMarkdown } from '@/utils/markdown';

const { t } = useI18n();

interface Props {
  agentAnswer?: string;
}

const props = defineProps<Props>();

// Define emits for communication with parent
const emit = defineEmits<{
  'translation-result': [result: string];
}>();

const isEditing = ref(false);
const editableText = ref(props.agentAnswer || '');
const lastEditedText = ref(props.agentAnswer || '');

// Local translation state for this component
const isTranslating = ref(false);
const translationError = ref<string>('');

// Generate unique component ID for translation callbacks
const componentId = `agent-response-${crypto.randomUUID()}`;

// Computed property to render markdown
const renderedMarkdown = computed(() => renderMarkdown(lastEditedText.value));

// Watch for changes in the prop to update local state
watch(
  () => props.agentAnswer,
  (newValue) => {
    if (!isEditing.value) {
      editableText.value = newValue || '';
      lastEditedText.value = newValue || ''; // Reset to new prop value
    }
  }
);

// Set up translation callbacks
onMounted(() => {
  BackendWebSocketService.registerTranslationCallback(componentId, {
    onLoading: (loading: boolean) => {
      isTranslating.value = loading;
    },
    onResult: (result: string) => {
      // Emit the translation result to parent (for ResponseToSendSection)
      emit('translation-result', result);

      // Reset UI state - exit edit mode and show the Dutch text that was translated
      isEditing.value = false;
      translationError.value = '';

      // Keep the Dutch text that was successfully translated
      lastEditedText.value = editableText.value;
    },
    onError: (error: string) => {
      translationError.value = error;
    },
  });
});

// Clean up translation callbacks
onUnmounted(() => {
  BackendWebSocketService.unregisterTranslationCallback(componentId);
});

const toggleEditMode = () => {
  if (isEditing.value) {
    // Cancel edit mode - revert to what was displayed before entering edit mode
    editableText.value = lastEditedText.value;
    // Clear local translation errors
    translationError.value = '';
  } else {
    // Enter edit mode - start with the last edited text
    editableText.value = lastEditedText.value;
  }
  isEditing.value = !isEditing.value;
};

const translateText = async () => {
  if (!editableText.value.trim()) {
    return;
  }

  translationError.value = '';

  try {
    const success = await BackendWebSocketService.sendTranslationRequest(
      editableText.value,
      componentId
    );

    if (!success) {
      translationError.value = 'Translation request failed';
      return;
    }

    // The response will be handled by the registered callbacks
  } catch (error) {
    console.error('Translation request failed:', error);
    translationError.value = 'Translation request failed';
  }
};
</script>
